/**
 * RoomSettingsPanel — "Who's talking" in the group sidebar (Step 11b, SPEC section 1).
 *
 *   Twin circles: Sitting out / In per twin, with a ring on a twin asked next (picked in
 *   the composer). Each circle carries the twin's profile card (ParticipantPopover): hover
 *   on desktop, the name below md.
 *
 * Viewers who cannot update settings see the circles disabled with one read-only line
 * (every circle tap is a save); they still pick twins through the composer's @ picker.
 * In the collapsed sidebar layout nothing is rendered.
 */

import { useTranslation } from 'react-i18next'

import { Text } from '@/components/ui/Text'
import { TwinCircles } from '@/components/chat/TwinCircles/TwinCircles'
import { TellerPicksController, useTellerPicks } from '@/hooks/useTellerPicks'

import { ChatRoomParticipantDto, ChatRoomSettings } from '@digital-twins/shared-types'

import { ParticipantPopover } from './ParticipantPopover'

interface RoomSettingsPanelProps {
  settings: ChatRoomSettings
  participants: Array<ChatRoomParticipantDto>
  /** The room's audience, used by the profile card to load the featured twin. */
  populationId?: string | null
  /** The page's picks controller; absent on read-only surfaces (a read-only view is derived). */
  tellerPicks?: TellerPicksController
  /**
   * The profile card's Ask action. The page owns its gate (signed-in viewers of an open
   * room): Ask only prefills an @mention for the next send, so a collaborator who sees
   * the settings read-only still gets it. Absent hides it.
   */
  onAskParticipant?: (participant: ChatRoomParticipantDto) => void
  collapsed?: boolean
  canUpdateSettings?: boolean
  isSaving?: boolean
  error?: string | null
}

export function RoomSettingsPanel(props: RoomSettingsPanelProps) {
  const {
    settings,
    participants,
    populationId = null,
    tellerPicks,
    onAskParticipant,
    collapsed = false,
    canUpdateSettings = true,
    isSaving = false,
    error = null,
  } = props

  const { t } = useTranslation('app')

  const readOnlyPicks = useTellerPicks({ participants, settings, canUpdateSettings: false })

  if (collapsed) {
    return null
  }

  const controller = tellerPicks ?? readOnlyPicks
  const isReadOnly = !canUpdateSettings || !controller.canUpdateSettings
  // Every circle tap is a save, so the circles follow the read-only gate.
  const isCirclesDisabled = isReadOnly

  return (
    <div className="flex flex-col gap-3" data-testid="room-settings-panel">
      <Text size="sm" className="font-bold leading-[1.4] text-twin-chat-fg">
        {t('roomPane.whoIsTalking')}
      </Text>

      <TwinCircles
        participants={participants}
        states={controller.states}
        onCycle={controller.cycle}
        isDisabled={isCirclesDisabled}
        renderProfile={(participant, trigger) => (
          <ParticipantPopover
            participant={participant}
            populationId={populationId}
            onAsk={onAskParticipant ? () => onAskParticipant(participant) : undefined}
          >
            {trigger}
          </ParticipantPopover>
        )}
      />

      {isReadOnly && (
        <Text
          size="sm"
          className="text-xs text-twin-chat-muted"
          data-testid="room-settings-read-only"
        >
          {t('roomPane.readOnly')}
        </Text>
      )}
      {isSaving && (
        <Text
          size="sm"
          className="text-xs text-twin-chat-muted"
          data-testid="room-settings-saving"
        >
          {t('roomPane.saving')}
        </Text>
      )}
      {error && (
        <Text
          size="sm"
          className="text-xs text-destructive"
          role="alert"
          data-testid="room-settings-error"
        >
          {error}
        </Text>
      )}
    </div>
  )
}
